import { Routes, Route, useNavigate, useLocation, useParams, useSearchParams, useSearchParams } from 'react-router-dom'
import React from 'react'
import Container from '@mui/material/Container'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Divider from '@mui/material/Divider'
import TextField from '@mui/material/TextField'
import FormControlLabel from '@mui/material/FormControlLabel'
import CircularProgress from '@mui/material/CircularProgress'
import SendIcon from '@mui/icons-material/Send'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import FileOpenIcon from '@mui/icons-material/FileOpen'
import PersonIcon from '@mui/icons-material/Person'
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail'

import { postToURL } from '~/client/lib/http'
import { useQuery, useCommand } from '~/client/hooks/cqrpc'
import { useLoginToAppAccount } from '~/client/hooks/appAccounts.js'
import { useCurrentUser } from '~/client/hooks/auth'
import ErrorMessage from '~/client/components/ErrorMessage'
import Link from '~/client/components/Link'
import CommandForm from '~/client/components/CommandForm'
import QuickSignupForm from '~/client/components/QuickSignupForm'
import ButtonRow from '~/client/components/ButtonRow'
import FullPageLoading from '~/client/components/FullPageLoading'
import FullPageMinimalLayout from '~/client/components/FullPageMinimalLayout'
import DataPalLogo from '~/client/components/DataPalLogo'
import AppIcon from '~/client/components/AppIcon'
import CurrentUserAvatar from '~/client/components/CurrentUserAvatar'
import AppAccountsList from '~/client/components/AppAccountsList'
import AppIconConnectToDataPalIcon from '~/client/components/AppIconConnectToDataPalIcon'
import InspectObject from '~/client/components/InspectObject'

const columnWidth = 'min(450px, calc(100vw - 20px))'

export const routeOptions = {
  topLevel: true
}

export default function Page() {
  const {host} = useParams()
  const [searchParams] = useSearchParams()
  const returnTo = searchParams.get('returnTo')
  const currentUserQuery = useCurrentUser()
  const appQuery = useQuery('apps.getByHost', {host})
  const loading = currentUserQuery.loading || appQuery.loading
  const loggedIn = !!currentUserQuery.currentUser
  const app = appQuery.result
  const props = {
    currentUser: currentUserQuery.currentUser,
    app: appQuery.result,
    returnTo,
    error: currentUserQuery.error || appQuery.error,
  }
  return loading
    ? <FullPageLoading/>
    : <FullPageMinimalLayout>
      {app
        ? loggedIn
          ? <PickOrCreateAppAccount {...props}/>
          : <LoginToDataPal {...props}/>
        : <div>app not found</div>
      }
    </FullPageMinimalLayout>
}

function LoginToDataPal({ currentUser, app, returnTo }){
  const location = useLocation()
  const searchParams = new URLSearchParams(location.searchParams)
  if (returnTo) searchParams.set('returnTo', returnTo)
  returnTo = `${location.pathname}?${searchParams}`

  return <>
    <Box sx={{
      flex: '1 1 20vh',
      display: 'flex',
      alignItems: 'center',
    }}>
      <DataPalLogo size="lg" sx={{m: 2}}/>
    </Box>
    <Paper sx={{p: 2, width: columnWidth}}>
      <Stack alignItems="center" sx={{textAlign: 'center'}}>
        <AppIcon app={app} size="lg" sx={{mb:2}}/>
        <Typography variant="h6" sx={{fontWeight: 100}}>
          {`Sign in to DataPal`}<br/>{`to continue to `}
          <strong>{app.host}</strong>
        </Typography>
      </Stack>
      <Divider sx={{my: 2}}/>
      <QuickSignupForm returnTo={returnTo}/>
    </Paper>
    <BorderBox sx={{textAlign: 'center'}}>
      {`New to DataPal?`}&nbsp;
      <Link to="/join?fun=42" returnTo>Create and account.</Link>
    </BorderBox>
  </>
}

function PickOrCreateAppAccount({ currentUser, app, returnTo }){
  const [authorized, setAuthorized] = React.useState(false)
  // const [nickname, setNickname] = React.useState()
  const loginToAppAccount = useLoginToAppAccount()
  const appAccountsQuery = useQuery('appAccounts.getForHost', { host: app.host })
  const appAccounts = appAccountsQuery.result
  const appAccount = (appAccounts || [])[0]
  console.log({ appAccounts, appAccount })
  // React.useEffect(
  //   () => {
  //     if (appAccounts) setNickname(`${app.host} #${appAccounts.length + 1}`)
  //   },
  //   [appAccounts]
  // )
  React.useEffect(
    () => {
      if (!appAccount) return
      loginToAppAccount.call({
        appAccountId: appAccount.id, returnTo
      })
    },
    [appAccount]
  )
  if (appAccountsQuery.loading || appAccount) return <FullPageLoading/>
  if (appAccountsQuery.error) return <ErrorMessage error={appAccountsQuery.error}/>


  // const submittable = !!(nickname)
  const submittable = true
  return <>
    <Stack
      sx={{flex: '1 1 20vh'}}
      direction="column"
      justifyContent="flex-end"
    >
      <AppIconConnectToDataPalIcon {...{ app }}/>
      <Typography variant="h6" sx={{my: 2, textAlign: 'center'}}>
        login to {app.host}
      </Typography>
    </Stack>

    <Paper sx={{p: 2, width: columnWidth}}>
      {appAccounts.length > 0
        ? <>
          <AppAccountsList {...{appAccounts}} loginButtonReturnTo={returnTo}/>
          <Divider sx={{my: 3, color: 'grey.400'}}>OR CREATE A NEW ACCOUNT</Divider>
        </>
        : null
      }
      <List sx={{p: 0}}>
        {
          [
            [
              <CurrentUserAvatar size="sm" sx={{m: 0}}/>,
              <><strong>{app.name}</strong> <small>({app.host})</small></>,
              'wants to store data in your DataPal',
            ],
            [
              <FileOpenIcon />,
              <strong>Add documents to your data store</strong>,
              'create new documents with read+write access',
            ],
            [
              <PersonIcon />,
              <strong>Access Your Basic Profile</strong>,
              'Avatar and Display Name (read-only)',
            ],
            [
              <AlternateEmailIcon />,
              <strong>Contact you via Email</strong>,
              `User your email address ${currentUser.email}`,
            ],
          ].map(([icon, primary, secondary], index) =>
            <ListItem key={index}>
              <ListItemIcon>{icon}</ListItemIcon>
              <ListItemText {...{primary, secondary}}/>
            </ListItem>
          )
        }
      </List>

      {/*<Divider sx={{my: 2}}/>*/}
      {/*<TextField {...{*/}
      {/*  fullWidth: true,*/}
      {/*  required: true,*/}
      {/*  size: 'small',*/}
      {/*  variant: 'outlined',*/}
      {/*  label: 'Nickname',*/}
      {/*  value: nickname ?? app.name,*/}
      {/*  onChange: setNickname,*/}
      {/*}}/>*/}
      <Divider sx={{my: 2}}/>

      <CommandForm {...{
        disabled: authorized,
        submittable,
        formButtons: false,
        command: 'appAccounts.create',
        cancelTo: `/accounts/${app.host}`,
        onSubmit(value, command) {
          command.call({ host: app.host, /*nickname,*/ returnTo })
        },
        onSuccess({ loginUrl }){
          setAuthorized(true)
          postToURL(loginUrl)
        },
      }}>{({ disabled }) =>
        <ButtonRow>
          <Button
            disabled={disabled}
            type="submit"
            variant="contained"
            color="success"
            sx={{flexGrow: 1}}
          >Create Account</Button>
          <Button
            disabled={disabled}
            variant="contained"
            color="secondary"
            sx={{flexGrow: 1}}
            component={Link}
            to={`https://${app.host}${returnTo}`}
          >Cancel</Button>
        </ButtonRow>
      }</CommandForm>

      <Stack sx={{mt: 1, color: 'grey.600'}} alignItems="center">
        <span>{`Authorizing will redirect to`}</span>
        <strong>{`https://${app.host}`}</strong>
      </Stack>
    </Paper>

    <BorderBox>
      <Stack
        direction={{sx: 'column', md: 'row'}}
        spacing={1}
        sx={{
          color: 'divider',
          fontSize: 'smaller',
        }}
      >
        {[
          [<SendIcon/>, 'Not Owned or operated by DataPal'],
          [<SendIcon/>, 'Created [time] ago '],
          [<SendIcon/>, 'Fewer than 10 DataPal users'],
        ].map(([icon, children], index) =>
          <Stack direction="row" spacing={1} key={index}>
            {icon}
            <div>{children}</div>
          </Stack>
        )}
      </Stack>
    </BorderBox>
  </>
}


function BorderBox({ children, ...props }){
  return <Box
   {...props}
    sx={{
      my: 2,
      p: 2,
      width: columnWidth,
      border: 1,
      borderRadius: 1,
      borderColor: 'divider',
      ...props.sx
    }}
  >{children}</Box>
}
